<template>
  <div class="page video-page">
    <xh-header />

    <div class="container-width vp-wrap" v-if="video">
      <!-- 顶部：返回 / 标题 / 完成状态 -->
      <div class="vp-top">
        <span class="mouse vp-back" @click="goBack">
          <i class="el-icon-arrow-left"></i> 返回课程
        </span>
        <h2 class="vp-title">
          {{ video.title }}
          <el-tag v-if="video.category" size="small" type="info" class="vp-cat">
            {{ categoryText(video.category) }}
          </el-tag>
          <el-tag v-if="finished" type="success" size="small" effect="dark" class="vp-done">
            <i class="el-icon-check"></i> 已完成
          </el-tag>
        </h2>
      </div>

      <!-- 视频简介（优先展示，无则不显示） -->
      <div class="vp-intro" v-if="video.introduction">
        <div class="intro-title">
          <i class="el-icon-document"></i> 视频简介
        </div>
        <div class="intro-html" v-html="video.introduction"></div>
      </div>

      <!-- 视频元信息（时长等，无则不展示） -->
      <div class="vp-meta" v-if="video.videoDuration">
        <span><i class="el-icon-time"></i> 视频时长 {{ formatDuration(video.videoDuration) }}</span>
      </div>

      <!-- 学习提示 -->
      <div class="vp-notice" :class="{ done: finished }">
        <template v-if="finished">
          <i class="el-icon-success"></i>
          您已完整观看本视频，学习记录已保存。可继续重复观看以巩固学习。
        </template>
        <template v-else>
          <i class="el-icon-warning-outline"></i>
          为保证学习效果，本视频<b>禁止拖拽进度</b>且<b>锁定 1.0× 倍速</b>，需完整观看至结尾才视为完成学习。
          <span v-if="duration">（已观看 {{ formatTime(maxPlayedTime) }} / {{ formatTime(duration) }}，{{ Math.floor(maxPercent) }}%）</span>
        </template>
      </div>

      <!-- 播放器（放在最下方） -->
      <div class="vp-player-block">
        <div class="block-head">
          <i class="el-icon-video-play"></i> 视频播放
        </div>
        <div class="player-shell" @keydown="onKeyDown" tabindex="0">
          <div class="player" ref="shell">
            <video
              ref="video"
              class="player-video"
              :src="video.videoUrl"
              preload="auto"
              playsinline
              disablePictureInPicture
              controlslist="nodownload nofullscreen noremoteplayback"
              @loadedmetadata="onMeta"
              @timeupdate="onTimeUpdate"
              @play="onPlay"
              @pause="onPause"
              @ended="onEnded"
              @ratechange="onRateChange"
              @seeking="onSeeking"
              @seeked="onSeeked"
              @error="onError"
              @click="togglePlay"
              @contextmenu.prevent
            ></video>

            <!-- 中央播放按钮 -->
            <div v-if="!playing && !ended && metaLoaded" class="play-overlay" @click="togglePlay">
              <i class="el-icon-video-play"></i>
            </div>
            <div v-if="!metaLoaded && !error" class="loading">
              <i class="el-icon-loading"></i> 加载视频中...
            </div>
            <div v-if="error" class="loading err">
              <i class="el-icon-warning-outline"></i> 视频加载失败，请稍后重试
            </div>

            <!-- 自绘控件条 -->
            <div class="ctrl-bar" v-if="metaLoaded && !error">
              <div class="ctrl-progress">
                <div class="bar-bg">
                  <div class="bar-played" :style="{ width: progressPercent + '%' }"></div>
                  <div class="bar-max" :style="{ left: maxPercent + '%' }" v-if="!finished"></div>
                </div>
              </div>
              <div class="ctrl-line">
                <span class="ctrl-btn" @click="togglePlay">
                  <i :class="playing ? 'el-icon-video-pause' : 'el-icon-video-play'"></i>
                </span>
                <span class="ctrl-time">
                  {{ formatTime(currentTime) }} / {{ formatTime(duration) }}
                </span>
                <span class="ctrl-vol">
                  <i :class="muted ? 'el-icon-close-notification' : 'el-icon-bell'" @click="toggleMute"></i>
                  <input type="range" min="0" max="100" v-model.number="volume" @input="onVolumeChange" class="vol-slider"/>
                </span>
                <span class="ctrl-flex"></span>
                <span class="ctrl-rate">1.0x</span>
                <span class="ctrl-btn" @click="toggleFullscreen">
                  <i class="el-icon-full-screen"></i>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-else-if="loaded" class="container-width">
      <div class="empty-block">视频不存在 <el-button size="small" @click="goBack">返回</el-button></div>
    </div>

    <xh-footer />
  </div>
</template>

<script>
import XhHeader from '../../../reveals/xh-header.vue'
import XhFooter from '../../../reveals/xh-footer.vue'
import { getStudyVideoList, recordStudyWatch } from '@/service/apis/study'

const CATEGORY_MAP = {
  1: '沙滩排球裁判',
  2: '室内排球裁判',
  3: '气排球裁判',
  4: '教练员内容'
}

// 允许的拖动容差(秒)。某些设备 timeupdate 节流较慢，给出 0.4s 容差以避免误回弹。
const SEEK_TOLERANCE = 0.4
const FINISH_THRESHOLD = 0.8

export default {
  name: 'StudyVideoPlayer',
  components: { XhHeader, XhFooter },
  data () {
    return {
      account: null,
      videoId: null,
      courseId: null,
      video: null,
      loaded: false,
      // 播放器状态
      metaLoaded: false,
      duration: 0,
      currentTime: 0,
      maxPlayedTime: 0,
      playing: false,
      ended: false,
      volume: 70,
      muted: false,
      finished: false,
      reported: false,  // 是否已回写过完成
      error: false
    }
  },
  computed: {
    progressPercent () {
      if (!this.duration) return 0
      return Math.min(100, (this.currentTime / this.duration) * 100)
    },
    maxPercent () {
      if (!this.duration) return 0
      return Math.min(100, (this.maxPlayedTime / this.duration) * 100)
    }
  },
  created () {
    const id = this.$route.query.id
    const cid = this.$route.query.courseId
    if (!id || !cid) {
      this.$message.error('参数缺失')
      this.$router.replace('/userinfo')
      return
    }
    this.videoId = Number(id)
    this.courseId = Number(cid)
    const acct = localStorage.getItem('ACCOUNT')
    if (!acct) {
      this.$router.replace('/login')
      return
    }
    this.account = JSON.parse(acct)
    // 通过课程视频列表定位当前视频（API 文档无单视频端点）
    getStudyVideoList.call(this, { courseId: this.courseId, pageIndex: 1, pageSize: 1000 }).then(res => {
      if (res && res.errCode === 9994) {
        localStorage.clear()
        this.$router.replace('/login')
        return
      }
      if (res.errCode === 0 && res.data) {
        const list = Array.isArray(res.data)
          ? res.data
          : (Array.isArray(res.data.records) ? res.data.records : [])
        this.video = list.find(v => v.id === this.videoId) || null
      }
      this.loaded = true
      // 初始化音量
      this.$nextTick(() => {
        if (this.$refs.video) {
          this.$refs.video.volume = this.volume / 100
        }
      })
    })
  },
  beforeDestroy () {
    // 离开页面前主动暂停，避免后台仍在播放
    if (this.$refs.video) {
      try { this.$refs.video.pause() } catch (e) { /* ignore */ }
    }
  },
  methods: {
    categoryText (c) { return CATEGORY_MAP[c] || '' },
    formatTime (sec) {
      sec = Math.floor(Number(sec) || 0)
      const m = Math.floor(sec / 60)
      const s = sec % 60
      return `${ String(m).padStart(2, '0') }:${ String(s).padStart(2, '0') }`
    },
    formatDuration (sec) {
      sec = Number(sec) || 0
      const m = Math.floor(sec / 60)
      const s = sec % 60
      return `${ m }分${ String(s).padStart(2, '0') }秒`
    },
    onMeta () {
      const v = this.$refs.video
      if (!v) return
      this.duration = v.duration || 0
      this.metaLoaded = true
      v.playbackRate = 1
    },
    onTimeUpdate () {
      const v = this.$refs.video
      if (!v) return
      this.currentTime = v.currentTime
      // 仅在向前播放且差距合理时更新最远位置（防止异常跳变把 maxPlayedTime 拉满）
      const next = v.currentTime
      if (next > this.maxPlayedTime && next - this.maxPlayedTime < 2) {
        this.maxPlayedTime = next
      } else if (next > this.maxPlayedTime && this.duration && (this.duration - next) < FINISH_THRESHOLD) {
        // 临近片尾的微小漂移直接接受
        this.maxPlayedTime = next
      }
      // 临近片尾即视为完成（部分浏览器不会触发 ended）
      if (this.duration && this.duration - next < FINISH_THRESHOLD && !this.finished) {
        this.markFinished()
      }
    },
    onSeeking () {
      const v = this.$refs.video
      if (!v) return
      // 只允许向后回看；超过 maxPlayedTime+容差 一律拉回
      if (v.currentTime > this.maxPlayedTime + SEEK_TOLERANCE) {
        v.currentTime = this.maxPlayedTime
        this.tipNoSeek()
      }
    },
    onSeeked () {
      const v = this.$refs.video
      if (!v) return
      if (v.currentTime > this.maxPlayedTime + SEEK_TOLERANCE) {
        v.currentTime = this.maxPlayedTime
        this.tipNoSeek()
      }
    },
    onRateChange () {
      const v = this.$refs.video
      if (!v) return
      if (v.playbackRate !== 1) {
        v.playbackRate = 1
      }
    },
    onPlay () { this.playing = true; this.ended = false },
    onPause () { this.playing = false },
    onEnded () {
      this.playing = false
      this.ended = true
      this.markFinished()
    },
    onError () {
      this.error = true
      this.metaLoaded = false
    },
    togglePlay () {
      const v = this.$refs.video
      if (!v) return
      if (v.paused) {
        const p = v.play()
        if (p && p.catch) p.catch(() => { /* 浏览器策略导致的播放失败，静默 */ })
      } else {
        v.pause()
      }
    },
    toggleMute () {
      this.muted = !this.muted
      if (this.$refs.video) {
        this.$refs.video.muted = this.muted
      }
    },
    onVolumeChange () {
      if (this.$refs.video) {
        this.$refs.video.volume = this.volume / 100
        if (this.volume > 0 && this.muted) {
          this.muted = false
          this.$refs.video.muted = false
        }
      }
    },
    toggleFullscreen () {
      const el = this.$refs.shell
      if (!el) return
      const isFull = document.fullscreenElement || document.webkitFullscreenElement
      if (!isFull) {
        if (el.requestFullscreen) el.requestFullscreen()
        else if (el.webkitRequestFullscreen) el.webkitRequestFullscreen()
      } else {
        if (document.exitFullscreen) document.exitFullscreen()
        else if (document.webkitExitFullscreen) document.webkitExitFullscreen()
      }
    },
    onKeyDown (e) {
      // 屏蔽与进度/倍速相关的快捷键
      const blocked = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
        ',', '.', 'j', 'l', 'J', 'L',
        '0', '1', '2', '3', '4', '5', '6', '7', '8', '9',
        'PageUp', 'PageDown', 'Home', 'End']
      if (blocked.indexOf(e.key) >= 0) {
        e.preventDefault()
        this.tipNoSeek()
      }
    },
    tipNoSeek () {
      // 简单防抖：1 秒内只提示一次
      if (this._tipTs && Date.now() - this._tipTs < 1500) return
      this._tipTs = Date.now()
      this.$message({
        type: 'warning',
        message: '为保证学习效果，本视频不允许快进',
        duration: 1500
      })
    },
    markFinished () {
      if (this.finished) return
      this.finished = true
      if (this.reported) return
      this.reported = true
      const watchDuration = Math.floor(this.duration || this.maxPlayedTime || 0)
      recordStudyWatch.call(this, {
        courseId: this.courseId,
        videoId: this.videoId,
        watchDuration
      }).then(res => {
        if (res && res.errCode === 9994) {
          localStorage.clear()
          this.$router.replace('/login')
          return
        }
        if (res && res.errCode === 0) {
          this.$message.success('已完成本视频学习')
        }
      }).catch(() => {
        // 失败时允许下次再尝试上报
        this.reported = false
      })
    },
    goBack () {
      this.$router.push('/study/course?id=' + this.courseId)
    }
  }
}
</script>

<style scoped>
.video-page { background: #fff; }
.vp-wrap {
  padding: 16px 0 60px;
  min-height: 60vh;
}

/* 顶部 */
.vp-top {
  border-bottom: solid 1px #f0f0f0;
  padding-bottom: 12px;
  margin-bottom: 18px;
}
.vp-back {
  display: inline-block;
  font-size: 13px;
  color: #888;
  padding: 4px 0 8px;
}
.vp-back:hover { color: #4c2ac9; }
.vp-title {
  font-size: 22px;
  color: #2e2e2e;
  font-weight: 600;
  line-height: 32px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}
.vp-title .vp-cat {
  font-weight: normal;
}
.vp-title .vp-done { margin-left: auto; }

/* 视频简介（主内容区，优先展示） */
.vp-intro {
  border: solid 1px #ece8ff;
  border-radius: 10px;
  padding: 20px 24px;
  margin-bottom: 16px;
  background: linear-gradient(135deg, #faf8ff 0%, #fff 60%);
}
.intro-title {
  font-size: 16px;
  color: #2e2e2e;
  font-weight: 600;
  margin-bottom: 14px;
  border-left: solid 3px #4c2ac9;
  padding-left: 10px;
}
.intro-title i { color: #4c2ac9; margin-right: 6px; }
.intro-html {
  font-size: 14px;
  color: #555;
  line-height: 26px;
  word-break: break-word;
}
.intro-html ::v-deep p { margin: 6px 0; }
.intro-html ::v-deep img { max-width: 100%; }

.vp-meta {
  font-size: 13px;
  color: #888;
  padding: 0 4px 14px;
}
.vp-meta i { margin-right: 4px; color: #4c2ac9; }

/* 学习提示 */
.vp-notice {
  font-size: 13px;
  color: #b35c00;
  background: #fff7e6;
  padding: 10px 14px;
  border-radius: 6px;
  line-height: 22px;
  margin-bottom: 16px;
}
.vp-notice b { color: #b35c00; }
.vp-notice.done {
  color: #2e8b57;
  background: #ecfaf3;
}
.vp-notice i { margin-right: 4px; }

/* 播放器区块（最下方） */
.vp-player-block {
  border: solid 1px #ece8ff;
  border-radius: 10px;
  overflow: hidden;
}
.block-head {
  font-size: 15px;
  color: #2e2e2e;
  font-weight: 600;
  padding: 12px 18px;
  border-bottom: solid 1px #f0ebff;
  background: #faf8ff;
}
.block-head i { color: #4c2ac9; margin-right: 6px; }
.player-shell { outline: none; }
.player {
  position: relative;
  width: 100%;
  background: #000;
  overflow: hidden;
  aspect-ratio: 16 / 9;
}
.player-video {
  width: 100%;
  height: 100%;
  display: block;
  background: #000;
  object-fit: contain;
}
.play-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  background: rgba(0, 0, 0, .25);
  color: #fff;
  font-size: 76px;
  transition: background .15s;
}
.play-overlay:hover { background: rgba(0, 0, 0, .35); }
.loading {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ddd;
  font-size: 14px;
  background: rgba(0,0,0,.4);
}
.loading.err { color: #ffb6b6; }

.ctrl-bar {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(to top, rgba(0,0,0,.75), rgba(0,0,0,0));
  padding: 30px 14px 10px;
  color: #fff;
  user-select: none;
}
.ctrl-progress { padding: 0 4px 8px; }
.bar-bg {
  position: relative;
  height: 4px;
  background: rgba(255,255,255,.25);
  border-radius: 2px;
  cursor: not-allowed;
}
.bar-played {
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  background: #4c2ac9;
  border-radius: 2px;
}
.bar-max {
  position: absolute;
  top: -2px;
  width: 2px;
  height: 8px;
  background: #fff;
  opacity: .55;
}
.ctrl-line {
  display: flex;
  align-items: center;
  font-size: 13px;
  gap: 12px;
}
.ctrl-btn {
  cursor: pointer;
  font-size: 22px;
  padding: 2px 4px;
}
.ctrl-btn:hover { color: #b39bff; }
.ctrl-time { font-family: 'Helvetica'; }
.ctrl-vol {
  display: flex;
  align-items: center;
  gap: 6px;
}
.ctrl-vol i { cursor: pointer; font-size: 16px; }
.vol-slider {
  width: 80px;
  cursor: pointer;
}
.ctrl-flex { flex: 1; }
.ctrl-rate {
  font-size: 12px;
  border: solid 1px rgba(255,255,255,.5);
  padding: 1px 8px;
  border-radius: 3px;
  cursor: not-allowed;
  opacity: .8;
}

.empty-block {
  text-align: center;
  color: #999;
  padding: 60px 20px;
  font-size: 14px;
}

@media screen and (max-width: 800px) {
  .vp-wrap { padding: 8px 12px 30px; }
  .vp-title { font-size: 17px; line-height: 26px; }
  .vp-title .vp-done { margin-left: 0; }
  .vp-intro { padding: 14px 16px; }
  .intro-title { font-size: 15px; }
  .intro-html { font-size: 13px; line-height: 24px; }
  .ctrl-line { gap: 8px; font-size: 12px; }
  .vol-slider { width: 50px; }
  .ctrl-btn { font-size: 18px; }
  .play-overlay { font-size: 56px; }
  .block-head { padding: 10px 14px; font-size: 14px; }
}
</style>
